import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import PageHeader from '@/components/shared/PageHeader';
import LoadingLock from '@/components/shared/LoadingLock';
import DataTable from '@/components/shared/DataTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SearchableSelect from '@/components/shared/SearchableSelect';
import {
  accountingApi,
  type BankAccountTransaction,
  type LookupBankAccount,
  type LookupBeneficiary,
  type LookupProject,
} from '@/lib/api/accounting';
import { formatRial } from '@/lib/formatMoney';

type LocalAttachmentMeta = {
  name: string;
  dataUrl: string;
  mimeType?: string;
};

const TX_ATTACHMENT_KEY = 'accounting_bank_tx_attachments_v1';

function readTxAttachmentMap(): Record<number, LocalAttachmentMeta> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(TX_ATTACHMENT_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<number, LocalAttachmentMeta>;
  } catch {
    return {};
  }
}

function saveTxAttachmentMap(map: Record<number, LocalAttachmentMeta>): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(TX_ATTACHMENT_KEY, JSON.stringify(map));
}

function formatThousandsInput(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function displayRialBalance(raw: number | string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return formatRial(0);
  const s = typeof raw === 'number' && Number.isFinite(raw) ? String(Math.trunc(raw)) : String(raw);
  const intPart = s.split('.')[0]?.replace(/\D/g, '') || '0';
  if (intPart.length <= 15) return formatRial(Number(intPart));
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const BankAccountDetailsPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const accountId = Number(id || 0);
  const [account, setAccount] = React.useState<LookupBankAccount | null>(null);
  const [transactions, setTransactions] = React.useState<BankAccountTransaction[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [depositAmount, setDepositAmount] = React.useState('');
  const [depositDescription, setDepositDescription] = React.useState('');
  const [depositSourceKind, setDepositSourceKind] = React.useState<'customer' | 'other'>('other');
  const [depositBeneficiaryId, setDepositBeneficiaryId] = React.useState('');
  const [depositProjectId, setDepositProjectId] = React.useState('');
  const [depositAttachment, setDepositAttachment] = React.useState<File | null>(null);
  const [depositAttachmentUrl, setDepositAttachmentUrl] = React.useState('');
  const [attachmentPreviewOpen, setAttachmentPreviewOpen] = React.useState(false);
  const [transactionPreviewOpen, setTransactionPreviewOpen] = React.useState(false);
  const [transactionPreviewName, setTransactionPreviewName] = React.useState('');
  const [transactionPreviewDataUrl, setTransactionPreviewDataUrl] = React.useState('');
  const [attachmentMap, setAttachmentMap] = React.useState<Record<number, LocalAttachmentMeta>>(
    () => readTxAttachmentMap(),
  );
  const [beneficiaries, setBeneficiaries] = React.useState<LookupBeneficiary[]>([]);
  const [projects, setProjects] = React.useState<LookupProject[]>([]);
  const [depositSubmitting, setDepositSubmitting] = React.useState(false);
  const [depositError, setDepositError] = React.useState('');
  const [depositSuccess, setDepositSuccess] = React.useState('');

  const reload = React.useCallback(() => {
    if (!accountId) return Promise.resolve();
    setLoading(true);
    return Promise.all([
      accountingApi.bankAccount(accountId),
      accountingApi.bankAccountTransactions(accountId),
      accountingApi.lookups(),
    ]).then(([a, t, lookups]) => {
        setAccount(a);
        setTransactions(t);
        setBeneficiaries(lookups.beneficiaries);
        setProjects(lookups.projects);
        return t;
      }).finally(() => setLoading(false));
  }, [accountId]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  React.useEffect(() => {
    return () => {
      if (depositAttachmentUrl) URL.revokeObjectURL(depositAttachmentUrl);
    };
  }, [depositAttachmentUrl]);

  const submitDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDepositError('');
    setDepositSuccess('');
    const n = Number(depositAmount.replace(/,/g, '').replace(/\D/g, ''));
    if (!n || n < 0.01) {
      setDepositError('مبلغ واریز را به‌درستی وارد کنید.');
      return;
    }
    if (depositSourceKind === 'customer' && !depositBeneficiaryId) {
      setDepositError('برای واریزی مشتری، انتخاب مشتری الزامی است.');
      return;
    }

    const selectedBeneficiary = beneficiaries.find((b) => b.id === depositBeneficiaryId);
    const selectedProject = projects.find((p) => String(p.id) === depositProjectId);
    const sourceLabel =
      depositSourceKind === 'customer'
        ? `مشتری: ${selectedBeneficiary?.name ?? 'نامشخص'}`
        : 'منبع: غیرمشتری/متفرقه';
    const projectLabel = selectedProject ? `پروژه: ${selectedProject.label}` : 'پروژه: عمومی';
    const attachmentLabel = depositAttachment ? `پیوست: ${depositAttachment.name}` : null;
    const note = depositDescription.trim() ? `شرح: ${depositDescription.trim()}` : null;
    const composedDescription = [sourceLabel, projectLabel, attachmentLabel, note]
      .filter(Boolean)
      .join(' | ');

    setDepositSubmitting(true);
    try {
      await accountingApi.adjustBankBalance(accountId, {
        amount: n,
        direction: 'credit',
        description: composedDescription,
      });
      const txRows = await reload();
      if (depositAttachment && depositAttachmentUrl && Array.isArray(txRows)) {
        const matched = [...txRows]
          .sort((a, b) => Number(b.id) - Number(a.id))
          .find(
            (x) =>
              x.direction === 'credit' &&
              Number(x.amount) === n &&
              (x.description || '') === composedDescription,
          );
        if (matched?.id) {
          setAttachmentMap((prev) => {
            const next = {
              ...prev,
              [matched.id]: {
                name: depositAttachment.name,
                dataUrl: depositAttachmentUrl,
                mimeType: depositAttachment.type,
              },
            };
            saveTxAttachmentMap(next);
            return next;
          });
        }
      }
      setDepositSuccess('سند واریز ثبت شد و موجودی به‌روز شد.');
      setDepositAmount('');
      setDepositDescription('');
      setDepositBeneficiaryId('');
      setDepositProjectId('');
      setDepositSourceKind('other');
      setDepositAttachment(null);
      if (depositAttachmentUrl) {
        URL.revokeObjectURL(depositAttachmentUrl);
        setDepositAttachmentUrl('');
      }
    } catch (err) {
      setDepositError(err instanceof Error ? err.message : 'ثبت سند واریز ناموفق بود.');
    } finally {
      setDepositSubmitting(false);
    }
  };

  const columns = React.useMemo<ColumnDef<BankAccountTransaction>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'زمان',
        cell: ({ row }) => row.original.created_at ? new Date(row.original.created_at).toLocaleString('fa-IR') : '-',
      },
      {
        accessorKey: 'direction',
        header: 'نوع',
        cell: ({ row }) => (
          <span className={`rounded-full px-2 py-1 text-xs ${row.original.direction === 'debit' ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}>
            {row.original.direction === 'debit' ? 'برداشت' : 'واریز'}
          </span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'شرح',
        cell: ({ row }) => {
          const raw = row.original.description || '';
          if (!raw) return '-';
          const parts = raw.split('|').map((x) => x.trim()).filter(Boolean);
          return (
            <div className="space-y-1">
              {parts.map((p, i) => (
                <p key={i} className="text-xs text-slate-700">
                  {p}
                </p>
              ))}
            </div>
          );
        },
      },
      { accessorKey: 'amount', header: 'مبلغ', cell: ({ row }) => <span className="font-mono">{formatRial(Number(row.original.amount || 0))}</span> },
      { accessorKey: 'balance_before', header: 'مانده قبل', cell: ({ row }) => <span className="font-mono">{formatRial(Number(row.original.balance_before || 0))}</span> },
      { accessorKey: 'balance_after', header: 'مانده بعد', cell: ({ row }) => <span className="font-mono">{formatRial(Number(row.original.balance_after || 0))}</span> },
      {
        id: 'actions',
        header: 'عملیات',
        cell: ({ row }) => {
          const meta = attachmentMap[row.original.id];
          if (!meta?.dataUrl) return <span className="text-xs text-slate-400">—</span>;
          return (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setTransactionPreviewName(meta.name);
                  setTransactionPreviewDataUrl(meta.dataUrl);
                  setTransactionPreviewOpen(true);
                }}
              >
                مشاهده
              </Button>
              <a
                href={meta.dataUrl}
                download={meta.name}
                className="inline-flex h-8 items-center rounded-md border px-3 text-xs hover:bg-slate-50"
              >
                دانلود
              </a>
            </div>
          );
        },
      },
    ],
    [attachmentMap],
  );

  const isPdfAttachment = depositAttachment?.type === 'application/pdf';

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title={account ? `گردش حساب: ${account.name}` : 'گردش حساب بانکی'}
        description="ریز تغییرات موجودی؛ برای افزایش مانده، سند واریز ثبت کنید."
        actions={
          <Button variant="outline" onClick={() => navigate('/bank-accounts')}>
            بازگشت
          </Button>
        }
      />

      <LoadingLock loading={loading}>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">اطلاعات حساب</CardTitle>
        </CardHeader>
        <CardContent>
          {account ? (
            <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
              <div><span className="text-slate-500">بانک:</span> {account.bank_name}</div>
              <div><span className="text-slate-500">صاحب حساب:</span> {account.account_holder_name}</div>
              <div><span className="text-slate-500">موجودی فعلی:</span> <span className="font-mono">{displayRialBalance(account.current_balance)} ریال</span></div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">{loading ? 'در حال بارگذاری...' : 'حساب یافت نشد.'}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">ثبت سند واریز به این حساب</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitDeposit} className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {depositError && (
              <div className="sm:col-span-2 lg:col-span-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{depositError}</div>
            )}
            {depositSuccess && (
              <div className="sm:col-span-2 lg:col-span-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{depositSuccess}</div>
            )}
            <div className="sm:col-span-2 lg:col-span-3">
              <p className="text-sm text-muted-foreground">
                با ثبت این فرم، یک ردیف واریز در گردش حساب ثبت می‌شود و موجودی افزایش می‌یابد (معادل سند واریز بانکی).
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">منبع واریز</label>
              <SearchableSelect
                value={depositSourceKind}
                onChange={(v) => {
                  const next = v as 'customer' | 'other';
                  setDepositSourceKind(next);
                  if (next !== 'customer') setDepositBeneficiaryId('');
                }}
                options={[
                  { value: 'other', label: 'غیرمشتری / متفرقه' },
                  { value: 'customer', label: 'از مشتری' },
                ]}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">پروژه (اختیاری)</label>
              <SearchableSelect
                value={depositProjectId}
                onChange={setDepositProjectId}
                options={[
                  { value: '', label: 'عمومی (بدون پروژه)' },
                  ...projects.map((p) => ({ value: String(p.id), label: p.label })),
                ]}
                placeholder="انتخاب پروژه"
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="mb-1 block text-xs text-slate-600">
                مشتری {depositSourceKind === 'customer' ? '(الزامی)' : '(اختیاری)'}
              </label>
              <SearchableSelect
                value={depositBeneficiaryId}
                onChange={setDepositBeneficiaryId}
                options={beneficiaries.map((b) => ({ value: b.id, label: b.name }))}
                placeholder={depositSourceKind === 'customer' ? 'انتخاب مشتری' : 'در صورت نیاز انتخاب کنید'}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">مبلغ واریز (ریال)</label>
              <Input
                dir="ltr"
                value={depositAmount}
                onChange={(e) => setDepositAmount(formatThousandsInput(e.target.value))}
                placeholder="مثلاً 50,000,000"
                disabled={!accountId || depositSubmitting}
              />
            </div>
            <div className="sm:col-span-1">
              <label className="mb-1 block text-xs text-slate-600">شرح سند (اختیاری)</label>
              <Input
                value={depositDescription}
                onChange={(e) => setDepositDescription(e.target.value)}
                placeholder="مثلاً: واریز صندوق"
                disabled={depositSubmitting}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">پیوست (PDF / عکس)</label>
              <Input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setDepositAttachment(f);
                  if (depositAttachmentUrl) URL.revokeObjectURL(depositAttachmentUrl);
                  setDepositAttachmentUrl(f ? URL.createObjectURL(f) : '');
                }}
                disabled={depositSubmitting}
              />
              <div className="mt-2 flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!depositAttachmentUrl}
                  onClick={() => setAttachmentPreviewOpen(true)}
                >
                  پیش‌نمایش
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!depositAttachment}
                  onClick={() => {
                    setDepositAttachment(null);
                    if (depositAttachmentUrl) URL.revokeObjectURL(depositAttachmentUrl);
                    setDepositAttachmentUrl('');
                  }}
                >
                  حذف پیوست
                </Button>
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <Button type="submit" disabled={!accountId || depositSubmitting || loading}>
                {depositSubmitting ? 'در حال ثبت...' : 'ثبت سند واریز'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">گردش حساب</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={transactions}
            emptyMessage={loading ? 'در حال بارگذاری...' : 'تراکنشی ثبت نشده است'}
          />
        </CardContent>
      </Card>
      </LoadingLock>

      <Dialog open={attachmentPreviewOpen} onOpenChange={setAttachmentPreviewOpen}>
        <DialogContent className="max-h-[85vh] max-w-4xl [&>button]:left-4 [&>button]:right-auto">
          <DialogHeader>
            <DialogTitle>پیش‌نمایش پیوست</DialogTitle>
          </DialogHeader>
          {!depositAttachmentUrl ? (
            <p className="text-sm text-slate-500">فایلی برای نمایش انتخاب نشده است.</p>
          ) : isPdfAttachment ? (
            <iframe src={depositAttachmentUrl} className="h-[70vh] w-full rounded-md border" title="PDF preview" />
          ) : (
            <img src={depositAttachmentUrl} alt="Attachment preview" className="max-h-[70vh] w-full rounded-md border object-contain" />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={transactionPreviewOpen} onOpenChange={setTransactionPreviewOpen}>
        <DialogContent className="max-h-[85vh] max-w-4xl [&>button]:left-4 [&>button]:right-auto">
          <DialogHeader>
            <DialogTitle>{transactionPreviewName || 'پیش‌نمایش پیوست سند'}</DialogTitle>
          </DialogHeader>
          {!transactionPreviewDataUrl ? (
            <p className="text-sm text-slate-500">فایلی برای نمایش وجود ندارد.</p>
          ) : transactionPreviewDataUrl.startsWith('data:application/pdf') ? (
            <iframe src={transactionPreviewDataUrl} className="h-[70vh] w-full rounded-md border" title="Transaction attachment preview" />
          ) : (
            <img src={transactionPreviewDataUrl} alt={transactionPreviewName || 'Attachment preview'} className="max-h-[70vh] w-full rounded-md border object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankAccountDetailsPage;
